import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import { LogoLoader } from "./components/common/LogoLoader";
import { NotFoundPage } from "./pages/NotFoundPage";

// Layout
import { Layout } from "./components/layout/Layout";

// Public Pages
import { HomePage } from "./pages/HomePage";
import { DoctorsPage } from "./pages/DoctorsPage";
import { DoctorProfilePage } from "./pages/DoctorProfilePage";
import { CategoryPage } from "./pages/CategoryPage";
import { CityPage } from "./pages/CityPage";
import { DoctorRegistrationPage } from "./pages/DoctorRegistrationPage";
import { RegistrationSuccessPage } from "./pages/RegistrationSuccessPage";
import { BlogsPage } from "./pages/BlogsPage";
import { BlogPostPage } from "./pages/BlogPostPage";
import { SEOLandingPage } from "./pages/SEOLandingPage";

// Doctor Dashboard & Admin Pages (code-split to keep the public bundle small)
const lazyNamed = (loader, name) => lazy(() => loader().then((m) => ({ default: m[name] })));
const DoctorLoginPage = lazyNamed(() => import("./pages/dashboard/DoctorLoginPage"), "DoctorLoginPage");
const DoctorDashboardPage = lazyNamed(() => import("./pages/dashboard/DoctorDashboardPage"), "DoctorDashboardPage");
const AdminLoginPage = lazyNamed(() => import("./pages/admin/AdminLoginPage"), "AdminLoginPage");
const AdminLayout = lazyNamed(() => import("./pages/admin/AdminLayout"), "AdminLayout");
const AdminDashboard = lazyNamed(() => import("./pages/admin/AdminDashboard"), "AdminDashboard");
const AdminDoctorsPage = lazyNamed(() => import("./pages/admin/AdminDoctorsPage"), "AdminDoctorsPage");
const AdminDoctorFormPage = lazyNamed(() => import("./pages/admin/AdminDoctorFormPage"), "AdminDoctorFormPage");
const AdminSpecialitiesPage = lazyNamed(() => import("./pages/admin/AdminSpecialitiesPage"), "AdminSpecialitiesPage");
const AdminLocationsPage = lazyNamed(() => import("./pages/admin/AdminLocationsPage"), "AdminLocationsPage");
const AdminSeoPagesPage = lazyNamed(() => import("./pages/admin/AdminSeoPagesPage"), "AdminSeoPagesPage");
const AdminReviewsPage = lazyNamed(() => import("./pages/admin/AdminReviewsPage"), "AdminReviewsPage");
const AdminInquiriesPage = lazyNamed(() => import("./pages/admin/AdminInquiriesPage"), "AdminInquiriesPage");
const AdminMarketingPage = lazyNamed(() => import("./pages/admin/AdminMarketingPage"), "AdminMarketingPage");
const AdminBlogsPage = lazyNamed(() => import("./pages/admin/AdminBlogsPage"), "AdminBlogsPage");
const AdminBlogEditorPage = lazyNamed(() => import("./pages/admin/AdminBlogEditorPage"), "AdminBlogEditorPage");
const AdminLandingEditorPage = lazyNamed(() => import("./pages/admin/AdminLandingEditorPage"), "AdminLandingEditorPage");
const AdminSiteSeoPage = lazyNamed(() => import("./pages/admin/AdminSiteSeoPage"), "AdminSiteSeoPage");
const AdminPageSeoEditorPage = lazyNamed(() => import("./pages/admin/AdminPageSeoEditorPage"), "AdminPageSeoEditorPage");
const AdminRedirectsPage = lazyNamed(() => import("./pages/admin/AdminRedirectsPage"), "AdminRedirectsPage");

function App() {
  return (
    <HelmetProvider>
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Suspense fallback={<LogoLoader label="Loading..." className="min-h-screen" />}>
        <Routes>
          {/* Public Routes */}
          <Route element={<Layout><HomePage /></Layout>} path="/" />
          <Route element={<Layout><DoctorsPage /></Layout>} path="/doctors" />
          <Route element={<Layout><DoctorProfilePage /></Layout>} path="/doctor/:slug" />
          <Route element={<Layout><CategoryPage /></Layout>} path="/category/:category" />
          <Route element={<Layout><CityPage /></Layout>} path="/city/:slug" />
          <Route element={<Layout><DoctorRegistrationPage /></Layout>} path="/register" />
          <Route element={<Layout><RegistrationSuccessPage /></Layout>} path="/registration-success" />
          <Route element={<Layout><BlogsPage /></Layout>} path="/blogs" />
          <Route element={<Layout><BlogPostPage /></Layout>} path="/blog/:slug" />
          <Route element={<Layout><SEOLandingPage /></Layout>} path="/landing/:slug" />

          {/* Doctor Dashboard Routes */}
          <Route path="/dashboard/login" element={<DoctorLoginPage />} />
          <Route path="/dashboard" element={<DoctorDashboardPage />} />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="doctors" element={<AdminDoctorsPage />} />
            <Route path="doctors/new" element={<AdminDoctorFormPage />} />
            <Route path="doctors/:id" element={<AdminDoctorFormPage />} />
            <Route path="specialities" element={<AdminSpecialitiesPage />} />
            <Route path="locations" element={<AdminLocationsPage />} />
            <Route path="seo-pages" element={<AdminSeoPagesPage />} />
            <Route path="reviews" element={<AdminReviewsPage />} />
            <Route path="inquiries" element={<AdminInquiriesPage />} />
            <Route path="marketing" element={<AdminMarketingPage />} />
            <Route path="landing/:id" element={<AdminLandingEditorPage />} />
            <Route path="blogs" element={<AdminBlogsPage />} />
            <Route path="blogs/new" element={<AdminBlogEditorPage />} />
            <Route path="blogs/:id" element={<AdminBlogEditorPage />} />
            <Route path="site-seo" element={<AdminSiteSeoPage />} />
            <Route path="site-seo/edit" element={<AdminPageSeoEditorPage />} />
            <Route path="redirects" element={<AdminRedirectsPage />} />
          </Route>
          <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;
